import React, { useEffect, useState } from 'react'
import Container from 'presentation/hospital/components/Container'
import * as S from './styles'
import { FinishOrderEdit } from 'presentation/hospital/components/FinishOrder'
import { ApiClientImplementation } from 'infra/api-client/api-client'
import { makeLocalStorageAdapter } from 'main/factories/cache/local-storage-adapter-factory'
import { PrepareApiRequestDecorator } from 'main/decorators/prepare-api-request-decorator/prepare-api-request-decorator'
import { ApiRepository } from 'repository/api-repository'
import { SurgicalOrderRepository } from 'repository/repositories/surgical-order/surgical-order-repository'
import { RemoteUpdateSurgicalOrder } from 'service/usecases/update-surgical-order/remote-update-surgical-order'
import { RemoteLoadSurgicalOrder } from 'service/usecases/load-surgical-order/remote-load-surgical-order'
import { useLocation } from 'react-router'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import { RemoteUploadSurgicalOrderDocument } from 'service/usecases/upload-surgical-order-document/remote-upload-surgical-order-document'
import LoadingModal from 'presentation/shared/components/LoadingModal'
import { RemoteLoadGuidesByType } from 'service/usecases/load-guides-by-type/remote-load-guides-by-type'
import { LoadGuidesByType } from '../../../../domain/usecases/surgical-order/load-guides-by-type'
import { RemoteLoadSurgicalOrderDocument } from 'service/usecases/load-surgical-order-document/load-upload-surgical-order-document'
import { RemoteGetFinalizeSurgicalOrdersHistory } from 'service/usecases/get-finalize-surgical-orders-history/remote-get-finalize-surgical-orders-history'
import { FinalizedOrderType } from 'domain/usecases/surgical-order/get-finalize-surgical-orders-history'

export default function FinishOrderLayout() {
  const apiClient = new ApiClientImplementation()
  const apiRepository = new PrepareApiRequestDecorator(
    makeLocalStorageAdapter(),
    new ApiRepository(apiClient)
  )

  const surgicalOrderRepository = new SurgicalOrderRepository(apiRepository)
  const loadSurgicalOrder = new RemoteLoadSurgicalOrder(surgicalOrderRepository)
  const updateSurgicalOrder = new RemoteUpdateSurgicalOrder(
    surgicalOrderRepository
  )
  const uploadSurgicalOrderDocument = new RemoteUploadSurgicalOrderDocument(
    surgicalOrderRepository
  )
  const loadSurgicalOrderDocument = new RemoteLoadSurgicalOrderDocument(
    surgicalOrderRepository
  )
  const loadGuidesType = new RemoteLoadGuidesByType(surgicalOrderRepository)

  const loadFinalizeSurgicalOrdersHistory =
    new RemoteGetFinalizeSurgicalOrdersHistory(surgicalOrderRepository)

  type SurgicalLocation = {
    id: number
  }

  const [isLoading, setIsLoading] = useState(false)
  const location = useLocation<SurgicalLocation>()
  const [order, setOrder] = useState({} as SurgicalOrderModel)
  const [guidesTypeProcedures, setGuidesTypeProcedures] = useState(
    {} as LoadGuidesByType.Model
  )
  const [guidesTypeOpme, setGuidesTypeOpme] = useState(
    {} as LoadGuidesByType.Model
  )

  const [finalizedOrders, setFinalizedOrders] = useState(
    {} as FinalizedOrderType[]
  )

  async function loadSurgicalOrderData() {
    setIsLoading(true)
    try {
      const loadOrder = await loadSurgicalOrder.load(
        Number(location.state.id),
        [
          'active_status',
          'doctor{name, doctor_id, crm, email, phone}',
          'patient{birthday, gender, company, name, cpf, email, lastName, phone, landlinePhone, maritalStatus}',
          'hospital{name, hospital_id}',
          'surgical_order_id',
          'createdAt',
          'expectedDate',
          'procedure {isMain,procedure_code,code,description,quantity,doctor_name, surgery_id, pro_fat_id}',
          'freeze',
          'healthInsurance {healthInsuranceCode, healthInsuranceName, healthPlanCode, healthPlanName, company, validThru, healthCard, healthSubPlanName, healthSubPlanCode}',
          'hospitalizationMode',
          'hospitalizationType',
          'cid {code, description}',
          'clinicalHistory',
          'technique',
          'laterality',
          'observation',
          'surgicalCenter',
          'room',
          'cti',
          'requestedDailies',
          'region',
          'patientHospitalized',
          'extraBlood',
          'allergy',
          'opme {solicitations {description, quantity}, providers}',
          'documents {document_id, type, name, documentUri, created_at}',
          'status {status}',
          `finalized {
            surgical_order_id
            finalize_surgical_order_id
            opme_details
            opme_observation
            procedure_observation
            password_to_finish
            password_max_date_validation
            password_max_string_validation
            finalized_by_user_id
            general_observation
            opme {
              solicitations {
                description
                quantity
                quantity_authorized
              }
              providers
            }
            opme_new {
              description
              provider
              quantity
              quantity_authorized
            }
            procedure {
              isMain
              procedure_code
              code
              description
              quantity
              quantity_authorized
              doctor_name
              surgery_id
              pro_fat_id
            }
            notifications {
              doctor {
                whatsapp
                email
              }
              patient {
                whatsapp
                email
              }
              secretary {
                whatsapp
                email
              }

              email {
                doctor
                patient
                secretary
              }
              phone {
                patient
                doctor
                secretary
              }
            }
            finalizedByUser {
              name
              email
              role
            }
            lastUpdateUser {
              name
              email
              role
            }
            created_at
            updated_at
          }`
        ]
      )

      setOrder(loadOrder)
    } catch (e) {
      return
    } finally {
      setIsLoading(false)
    }
  }

  const loadGuidesTypeProcedures = async () => {
    try {
      const guides = await loadGuidesType.load(Number(location.state.id), 'I', {
        fields: [
          'date',
          'password',
          'details{quantity_solicitation, quantity_authorization,provider, procedure, fat_id, guideBusiness{fat_id, cbhpm_id, description_cbhpm}}'
        ]
      })

      setGuidesTypeProcedures(guides)
    } catch (e) {
      return
    }
  }

  const loadGuidesTypeOpme = async () => {
    try {
      const guides = await loadGuidesType.load(Number(location.state.id), 'O', {
        fields: [
          'date',
          'password',
          'details{quantity_solicitation, quantity_authorization, provider,procedure, fat_id, guideBusiness{fat_id, cbhpm_id, description_cbhpm}}'
        ]
      })

      setGuidesTypeOpme(guides)
    } catch (e) {
      return
    }
  }

  const loadFinalizeSurgicalOrderHistory = async () => {
    try {
      const response = await loadFinalizeSurgicalOrdersHistory.load({
        surgical_order_id: location.state.id
      })
      setFinalizedOrders(response.data)
    } catch (e) {
      return
    }
  }

  useEffect(() => {
    loadSurgicalOrderData()
    loadGuidesTypeProcedures()
    loadGuidesTypeOpme()
    loadFinalizeSurgicalOrderHistory()
  }, [])

  if (isLoading) {
    return <LoadingModal show={isLoading} />
  }

  if (order?.procedure) {
    return (
      <Container>
        <S.Wrapper>
          <FinishOrderEdit
            guidesTypeProcedures={guidesTypeProcedures}
            guidesTypeOpme={guidesTypeOpme}
            updateSurgicalOrder={updateSurgicalOrder}
            surgicalOrder={order}
            uploadSurgicalOrderDocument={uploadSurgicalOrderDocument}
            loadSurgicalOrderDocument={loadSurgicalOrderDocument}
            finalizedOrders={finalizedOrders}
          />
        </S.Wrapper>
      </Container>
    )
  } else return <></>
}
